import { SupportTicket } from '../../models/supportTickets';

export const priorityOptions = [
  { label: 'Urgent', value: 'urgent' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
  { label: 'None', value: 'none' },
];

export const productAreaOptions = [
  { label: 'Credit', value: 'Credit' },
  { label: 'VoX', value: 'VoX' },
  { label: 'Fees', value: 'Fees' },
  { label: 'Pricing', value: 'Pricing' },
  { label: 'AUW', value: 'AUW' },
  { label: 'Other', value: 'Other' },
];

type Severity = 'danger' | 'warning' | 'info' | 'success' | 'secondary' | 'contrast' | null;

export const prioritySeverity = (priority: string): Severity => {
  switch (priority?.toLowerCase()) {
    case 'urgent':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return null;
  }
};

export const priorityDotClass = (priority: string): string => {
  switch (priority?.toLowerCase()) {
    case 'urgent':
      return 'bg-red-500';
    case 'high':
      return 'bg-orange-500';
    case 'medium':
      return 'bg-amber-400';
    case 'low':
      return 'bg-sky-400';
    default:
      return 'bg-gray-300';
  }
};

export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
};

export const isClosedStatus = (status: string): boolean => {
  const normalized = status?.toLowerCase() ?? '';
  return (
    normalized.includes('done') ||
    normalized.includes('complete') ||
    normalized.includes('cancel') ||
    normalized.includes('closed')
  );
};

export const statusSeverity = (status: string): Severity => {
  const normalized = status?.toLowerCase() ?? '';
  if (normalized.includes('cancel')) return 'danger';
  if (
    normalized.includes('done') ||
    normalized.includes('complete') ||
    normalized.includes('closed') ||
    normalized.includes('resolved')
  )
    return 'success';
  if (
    normalized.includes('progress') ||
    normalized.includes('started') ||
    normalized.includes('investigat')
  )
    return 'info';
  if (
    normalized.includes('wait') ||
    normalized.includes('block') ||
    normalized.includes('hold') ||
    normalized.includes('review') ||
    normalized.includes('pending')
  )
    return 'warning';
  return 'secondary';
};

export const statusDotClass = (status: string): string => {
  switch (statusSeverity(status)) {
    case 'danger':
      return 'bg-red-500';
    case 'success':
      return 'bg-green-500';
    case 'info':
      return 'bg-sky-500';
    case 'warning':
      return 'bg-amber-400';
    case 'contrast':
      return 'bg-slate-700';
    default:
      return 'bg-gray-400';
  }
};

const escapeCsvCell = (value: string): string => {
  const needsQuotes = /[",\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
};

export const buildTicketsCsv = (tickets: SupportTicket[]): string => {
  const headers = [
    'Identifier',
    'Title',
    'Status',
    'Priority',
    'Assignee',
    'Comments',
    'Created',
    'Updated',
  ];
  const rows = tickets.map((ticket) =>
    [
      ticket.identifier,
      ticket.title,
      ticket.status,
      ticket.priority,
      ticket.assigneeName ?? '',
      String(ticket.commentCount),
      new Date(ticket.createdAt).toISOString(),
      new Date(ticket.updatedAt).toISOString(),
    ]
      .map((cell) => escapeCsvCell(cell))
      .join(',')
  );
  return [headers.join(','), ...rows].join('\r\n');
};

export const downloadTicketsCsv = (tickets: SupportTicket[]): void => {
  const csv = buildTicketsCsv(tickets);
  const today = new Date().toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `tickets-export-${today}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
